import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import { BRAND_LOGO_512_URL } from '../../config/brandAssets';
import './TercerSectorPage.css';

const TercerSectorPage = ({ darkMode, toggleDarkMode }) => {
  const canonicalUrl = 'https://e-asy.es/tercer-sector';

  const retos = [
    {
      titulo: 'Subvenciones y justificación',
      descripcion:
        'Evidencias dispersas, tiempos ajustados y mucho trabajo manual al final del proyecto.',
    },
    {
      titulo: 'Datos sensibles y RGPD',
      descripcion:
        'Beneficiarios, menores, salud o dependencia requieren control de accesos y buenas prácticas.',
    },
    {
      titulo: 'Coordinación de equipos',
      descripcion:
        'Personal, voluntariado y múltiples programas sin herramientas claras para coordinarse.',
    },
    {
      titulo: 'Impacto y trazabilidad',
      descripcion:
        'Necesitas indicadores y reporting para financiadores, auditorías y toma de decisiones.',
    },
  ];

  const servicios = [
    {
      titulo: 'Diagnóstico y hoja de ruta',
      descripcion:
        'Analizamos procesos y priorizamos iniciativas para que la digitalización sea viable y medible.',
    },
    {
      titulo: 'Sistemas de evidencias y subvenciones',
      descripcion:
        'Estructura documental, capturas operativas y flujos para justificar sin estrés y con control.',
    },
    {
      titulo: 'CRM para donantes, socios y voluntariado',
      descripcion:
        'Datos unificados, segmentación, campañas y seguimiento con trazabilidad y cumplimiento.',
    },
    {
      titulo: 'Automatización de procesos',
      descripcion:
        'Recordatorios, aprobaciones, formularios y tareas repetitivas automatizadas para ahorrar horas.',
    },
    {
      titulo: 'Data & BI para medir impacto',
      descripcion:
        'Indicadores por programa, cuadros de mando y reporting para dirección y financiadores.',
    },
    {
      titulo: 'Ciberseguridad y cumplimiento',
      descripcion:
        '2FA, accesos, copias, permisos y políticas prácticas para proteger datos sensibles.',
    },
    {
      titulo: 'Formación y adopción',
      descripcion:
        'Acompañamos al equipo para que las herramientas se usen bien y se mantengan en el tiempo.',
    },
  ];

  const metodologia = [
    {
      paso: '1',
      titulo: 'Diagnóstico',
      descripcion:
        'Entendemos programas, procesos y datos. Identificamos riesgos y oportunidades con impacto real.',
    },
    {
      paso: '2',
      titulo: 'Prioridad y diseño',
      descripcion:
        'Definimos quick wins y roadmap. Elegimos herramientas y un modelo de datos mínimo útil.',
    },
    {
      paso: '3',
      titulo: 'Implantación',
      descripcion:
        'Configuración, integraciones y puesta en marcha por fases para minimizar fricción y riesgos.',
    },
    {
      paso: '4',
      titulo: 'Formación',
      descripcion:
        'Capacitamos al equipo con casos reales. Documentamos el “cómo se hace aquí”.',
    },
    {
      paso: '5',
      titulo: 'Mejora continua',
      descripcion:
        'Medimos, revisamos y ajustamos. La digitalización es un sistema vivo, no una entrega puntual.',
    },
  ];

  const indicadores = [
    'Personas atendidas por programa y periodo',
    'Cumplimiento de hitos y entregables',
    'Evidencias pendientes y riesgos',
    'Tiempos de respuesta y carga administrativa',
    'Evolución de captación y retención (socios/donantes/voluntariado)',
  ];

  const guias = [
    {
      titulo: 'Digitalización en el tercer sector: guía 2026',
      descripcion: 'Hoja de ruta por fases (fundamentos, operación, datos e impacto, automatización).',
      to: '/blog/digitalizacion-tercer-sector-canarias-guia-2026',
    },
    {
      titulo: 'CRM para ONG (donantes, socios y voluntariado)',
      descripcion: 'Cómo centralizar relaciones y campañas sin perder trazabilidad ni cumplimiento.',
      to: '/blog/crm-ong-gestion-donantes-voluntariado-proyectos',
    },
    {
      titulo: 'Subvenciones: evidencias y justificación sin estrés',
      descripcion: 'Un sistema de evidencias desde el día 1 para reducir horas y riesgos.',
      to: '/blog/automatizar-subvenciones-tercer-sector-trazabilidad-evidencias',
    },
    {
      titulo: 'RGPD y ciberseguridad en entidades sociales',
      descripcion: 'Checklist práctico para proteger datos sensibles sin frenar la intervención.',
      to: '/blog/rgpd-ciberseguridad-tercer-sector-datos-sensibles',
    },
  ];

  const faqItems = [
    {
      q: '¿Qué significa “digitalización con acompañamiento” en el tercer sector?',
      a: 'Que no solo implantamos herramientas: analizamos procesos, definimos una hoja de ruta, formamos al equipo y damos soporte para que se use bien y se mantenga en el tiempo.',
    },
    {
      q: '¿Podemos empezar sin un gran presupuesto?',
      a: 'Sí. Priorizamos quick wins (documentación, accesos, evidencias, automatizaciones simples) y escalamos por fases según el impacto y la capacidad del equipo.',
    },
    {
      q: '¿Qué herramientas suelen implantar?',
      a: 'Depende del contexto. Lo importante es el sistema (procesos + datos + permisos). Trabajamos con soluciones cloud, CRM, automatización y BI cuando aportan valor real.',
    },
    {
      q: '¿Cómo mejoran la justificación de subvenciones?',
      a: 'Diseñamos un sistema de evidencias: estructura documental, plantillas, captura operativa (incl. móvil), recordatorios y control de versiones para que la justificación sea un resultado natural del trabajo.',
    },
    {
      q: '¿Cómo tratan el RGPD y los datos sensibles?',
      a: 'Aplicamos un enfoque práctico: roles y permisos, 2FA, copias verificadas, políticas mínimas de dispositivos y formación. Priorizamos riesgo real y adopción del equipo.',
    },
    {
      q: '¿Solo trabajan en Canarias?',
      a: 'Estamos en Canarias, pero podemos acompañar a entidades de toda España. La clave es adaptar la implantación a tu equipo y tu operativa.',
    },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: 'https://e-asy.es/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Tercer Sector',
        item: canonicalUrl,
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Digitalización y acompañamiento para el Tercer Sector',
    serviceType: 'Consultoría tecnológica, implantación y acompañamiento',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
      logo: BRAND_LOGO_512_URL,
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Canarias' },
      { '@type': 'Country', name: 'España' },
    ],
    url: canonicalUrl,
    description:
      'Digitalización con acompañamiento para ONG, asociaciones y fundaciones: subvenciones y evidencias, CRM, automatización, BI e indicadores de impacto, RGPD y ciberseguridad.',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  const structuredData = [breadcrumbSchema, serviceSchema, faqSchema];

  return (
    <>
      <SEO
        title="Digitalización para ONG, asociaciones y fundaciones | E-ASY"
        description="Acompañamiento para ONG y fundaciones: subvenciones, CRM, indicadores y RGPD. Proyectos en remoto para entidades de toda España, con sede en Tenerife."
        keywords="tercer sector, digitalización ONG Canarias, digitalización fundaciones Canarias, digitalización asociaciones Canarias, acompañamiento digital, subvenciones, justificación, evidencias, CRM ONG, donantes, socios, voluntariado, impacto social, trazabilidad, RGPD, ciberseguridad, Canarias, Tenerife, Gran Canaria"
        canonical={canonicalUrl}
        image={BRAND_LOGO_512_URL}
      />

      <AnalyticsTracker pageTitle="Tercer Sector - E-ASY" pageLocation="/tercer-sector" darkMode={darkMode} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className={`tercer-sector-page ${darkMode ? 'dark' : 'light'}`}>
        <nav className="tercer-sector-breadcrumbs" aria-label="Migas de pan">
          <Link to="/">Inicio</Link>
          <span aria-hidden="true">/</span>
          <span>Tercer Sector</span>
        </nav>

        <section className="tercer-sector-hero">
          <div className="tercer-sector-hero-content">
            <div className="tercer-sector-hero-text">
              <span className="tercer-sector-badge">Tercer sector</span>
              <h1 className="tercer-sector-hero-title">
                Digitalización con acompañamiento para entidades sociales
              </h1>
              <p className="tercer-sector-hero-subtitle">
                Ayudamos a <strong>ONG</strong>, <strong>asociaciones</strong> y <strong>fundaciones</strong> a reducir
                carga administrativa, mejorar trazabilidad e indicadores, y proteger datos sensibles. Con un enfoque
                práctico: procesos, herramientas y adopción.
              </p>
              <p className="tercer-sector-hero-subtitle">
                Especialistas en digitalización en <strong>Canarias</strong> (Tenerife y Gran Canaria) y proyectos en
                toda España.
              </p>
              <div className="tercer-sector-hero-cta">
                <Link to="/contacto" className="tercer-sector-button primary">
                  Solicitar consultoría gratuita
                </Link>
                <Link to="/blog" className="tercer-sector-button secondary">
                  Ver recursos y guías
                </Link>
              </div>
              <div className="tercer-sector-hero-notes">
                <div className="note">
                  <span className="note-kpi">Enfoque</span>
                  <span className="note-text">impacto y sostenibilidad</span>
                </div>
                <div className="note">
                  <span className="note-kpi">Prioridad</span>
                  <span className="note-text">trazabilidad y evidencias</span>
                </div>
                <div className="note">
                  <span className="note-kpi">Base</span>
                  <span className="note-text">RGPD y seguridad</span>
                </div>
              </div>
            </div>

            <div className="tercer-sector-hero-visual" aria-hidden="true">
              <div className="tercer-sector-visual-card">
                <div className="visual-row">
                  <span className="visual-pill">Programas</span>
                  <span className="visual-pill">Evidencias</span>
                  <span className="visual-pill">Impacto</span>
                </div>
                <div className="visual-metrics">
                  <div className="metric">
                    <div className="metric-title">Seguimiento</div>
                    <div className="metric-value">Centralizado</div>
                  </div>
                  <div className="metric">
                    <div className="metric-title">Justificación</div>
                    <div className="metric-value">Por diseño</div>
                  </div>
                  <div className="metric">
                    <div className="metric-title">Datos</div>
                    <div className="metric-value">Con permisos</div>
                  </div>
                </div>
                <div className="visual-bar">
                  <div className="bar-fill" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="tercer-sector-section">
          <div className="tercer-sector-container">
            <header className="tercer-sector-section-header">
              <h2>Guías para el tercer sector</h2>
              <p>
                Contenido práctico para equipos de gestión, coordinación y dirección: subvenciones, evidencias, CRM,
                seguridad y medición de impacto.
              </p>
            </header>

            <div className="tercer-sector-grid guides">
              {guias.map((guia) => (
                <Link key={guia.to} to={guia.to} className="tercer-sector-card guide">
                  <h3>{guia.titulo}</h3>
                  <p>{guia.descripcion}</p>
                  <span className="guide-cta">Leer guía →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="tercer-sector-section">
          <div className="tercer-sector-container">
            <header className="tercer-sector-section-header">
              <h2>Retos habituales (y cómo los resolvemos)</h2>
              <p>
                La digitalización en el tercer sector funciona cuando simplifica el día a día y mejora la trazabilidad
                sin burocracia extra.
              </p>
            </header>

            <div className="tercer-sector-grid">
              {retos.map((item) => (
                <div key={item.titulo} className="tercer-sector-card">
                  <h3>{item.titulo}</h3>
                  <p>{item.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="tercer-sector-section alt">
          <div className="tercer-sector-container">
            <header className="tercer-sector-section-header">
              <h2>Servicios para entidades sociales</h2>
              <p>
                No vendemos “herramientas”. Alineamos procesos, datos y adopción para que la tecnología sostenga tu
                misión.
              </p>
            </header>

            <div className="tercer-sector-grid services">
              {servicios.map((servicio) => (
                <div key={servicio.titulo} className="tercer-sector-card service">
                  <h3>{servicio.titulo}</h3>
                  <p>{servicio.descripcion}</p>
                  <div className="service-links" aria-label="Enlaces relacionados">
                    {servicio.titulo === 'Diagnóstico y hoja de ruta' && (
                      <Link to="/servicios/consultoria-tecnologica" className="service-link">
                        Consultoría tecnológica →
                      </Link>
                    )}
                    {servicio.titulo === 'Automatización de procesos' && (
                      <Link to="/vulpin" className="service-link">
                        Vulpin Suite →
                      </Link>
                    )}
                    {servicio.titulo === 'Data & BI para medir impacto' && (
                      <Link to="/servicios/bi-explotacion-datos" className="service-link">
                        BI y explotación de datos →
                      </Link>
                    )}
                    {servicio.titulo === 'Ciberseguridad y cumplimiento' && (
                      <Link to="/servicios/ciberseguridad-cumplimiento" className="service-link">
                        Ciberseguridad y cumplimiento →
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="tercer-sector-cta-row">
              <Link to="/contacto" className="tercer-sector-button primary">
                Hablar con un especialista
              </Link>
              <Link to="/servicios" className="tercer-sector-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>

        <section className="tercer-sector-section">
          <div className="tercer-sector-container">
            <header className="tercer-sector-section-header">
              <h2>Nuestra forma de trabajar</h2>
              <p>Metodología simple: diagnóstico, prioridad, implantación y acompañamiento hasta que se use bien.</p>
            </header>

            <div className="tercer-sector-steps">
              {metodologia.map((step) => (
                <div key={step.paso} className="step">
                  <div className="step-number">{step.paso}</div>
                  <div className="step-body">
                    <h3>{step.titulo}</h3>
                    <p>{step.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="tercer-sector-section alt">
          <div className="tercer-sector-container">
            <header className="tercer-sector-section-header">
              <h2>Indicadores que solemos dejar listos</h2>
              <p>Sin “dashboarditis”: lo mínimo útil para dirección, equipos y financiadores.</p>
            </header>

            <ul className="tercer-sector-list">
              {indicadores.map((kpi) => (
                <li key={kpi}>{kpi}</li>
              ))}
            </ul>

            <div className="tercer-sector-resources">
              <h3>Recursos</h3>
              <p>
                En el blog tienes guías específicas. Entra en <strong>Blog</strong> y filtra por la categoría{' '}
                <strong>Tercer Sector</strong>.
              </p>
              <Link to="/blog" className="tercer-sector-link">
                Ir al blog
              </Link>
            </div>
          </div>
        </section>

        <section className="tercer-sector-section">
          <div className="tercer-sector-container">
            <header className="tercer-sector-section-header">
              <h2>Preguntas frecuentes</h2>
              <p>Respuestas rápidas para entidades sociales que están valorando dar el siguiente paso.</p>
            </header>

            <div className="tercer-sector-faq" role="list">
              {faqItems.map((item) => (
                <details key={item.q} className="faq-item">
                  <summary className="faq-question">{item.q}</summary>
                  <div className="faq-answer">
                    <p>{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="tercer-sector-final">
          <div className="tercer-sector-container final">
            <h2>¿Hablamos de tu caso?</h2>
            <p>
              Si quieres una hoja de ruta clara para digitalizar con enfoque en impacto, trazabilidad y seguridad, te
              ayudamos a empezar con buen pie.
            </p>
            <div className="tercer-sector-cta-row">
              <Link to="/contacto" className="tercer-sector-button primary">
                Solicitar consultoría gratuita
              </Link>
              <a href="tel:+34822685012" className="tercer-sector-button secondary">
                Llamar: +34 822 68 50 12
              </a>
            </div>
          </div>
        </section>
      </div>

      <CoberturaNota />
      <Footer darkMode={darkMode} />
    </>
  );
};

export default TercerSectorPage;

